import {
  AnalysisCode,
  CompletionFieldDef,
  ValidValue,
  WorkOrderEvents,
} from "../models/analysiscodes/AnalysisCodes";
import { getCurrentPlant } from "../util/ApiOptionsHelper";
import { getEvents, updateEvents } from "./Events";
import { getColumnsWithGroup, getCompleteColumnDetailList } from "./Windows";
import { getCascadeValues } from "./Validations";
import { getOptionByIdWithCORP } from "./TabwareOptions";
import { useWorkOrderStore } from "../store/wo-order";
import { isCascadeChildRequired } from "../util/MetadataHelper";
import { getCascadeRequiredValuesForPlant } from "../util/StoresAccountHelper";

export const getCCAFieldDefinitions = async (equipment?: string) => {
  const data = {
    WindowName: "mob_completion_cca",
    IncludeValidValues: "true",
    IncludeValidValuesExceptions: [],
    ValidValueFilters: {
      Equipment: equipment || "",
      Plant: await getCurrentPlant(),
    },
  };
  const response = await getColumnsWithGroup(data);
  const componentField: CompletionFieldDef | undefined =
    response.data?.fields?.["eq_events_completion.component"];
  const conditionField: CompletionFieldDef | undefined =
    response.data?.fields?.["eq_events_completion.condition"];
  const actionField: CompletionFieldDef | undefined =
    response.data?.fields?.["eq_events_completion.action"];
  return {
    componentField,
    conditionField,
    actionField,
  };
};

export const getMappingSource = async () => {
  const response = await getOptionByIdWithCORP("CCAMappingSource");
  const relationship = response.data?.PlantValue || response.data?.DefaultValue;
  return relationship as string | undefined;
};

export const getCCACascadeValues = async () => {
  //in case of offline mode use the cascade values from cache
  let cascadeValues = useWorkOrderStore.getState().cascadeValues;
  if (!cascadeValues) {
    const response = await getCascadeValues();
    cascadeValues = response.data;
  }
  /* Bug 873321 - a cascade value that is not a list is no cascade mapping at all, and must not
     take the Analysis Codes screen down with it. Guarded here as well as at the point it is
     stored, because a bad value may already be sitting in persisted state. */
  if (!Array.isArray(cascadeValues)) {
    cascadeValues = [];
  }
  const componentCascadeValues =
    cascadeValues?.filter((validValue) => {
      return validValue.ParentField === "EquipmentClass";
    }) || [];
  const conditionCascadeValues =
    cascadeValues?.filter((validValue) => {
      return validValue.ParentField === "Component";
    }) || [];
  const actionCascadeValues =
    cascadeValues?.filter((validValue) => {
      return validValue.ParentField === "Condition";
    }) || [];
  return {
    componentCascadeValues,
    conditionCascadeValues,
    actionCascadeValues,
  };
};

/**
 * US 906024 - the cascade "required" rules that apply to the Component / Condition / Action
 * chain, for the codes currently selected.
 *
 * The chain is EquipmentClass -> Component -> Condition -> Action, the same relationships
 * getCCACascadeValues filters the pick lists by, so a Required flag configured against any of
 * them is answered here. Action is qualified by its ancestor (the Component) exactly as its
 * value mapping is, so a rule set at one level cannot leak into another.
 */
export const getCCARequiredFlags = async (selected: {
  equipmentClass?: string;
  component?: string;
  condition?: string;
}) => {
  const requiredValues = await getCascadeRequiredValuesForPlant();
  return {
    componentRequired: isCascadeChildRequired(
      requiredValues,
      "EquipmentClass",
      selected.equipmentClass
    ),
    conditionRequired: isCascadeChildRequired(
      requiredValues,
      "Component",
      selected.component
    ),
    actionRequired: isCascadeChildRequired(
      requiredValues,
      "Condition",
      selected.condition,
      selected.component
    ),
  };
};

export const getCompleteColumnDetails = async (equipment?: string) => {
  const response = await getCompleteColumnDetailList({
    TableName: "eq_events_completion",
    IncludeValidValues: "true",
    ValidValueFilters: {
      Plant: await getCurrentPlant(),
      Equipment: equipment,
    },
  });
  const component = response.data?.Component?.ValidValues as ValidValue[];
  const condition = response.data?.Condition?.ValidValues as ValidValue[];
  const action = response.data?.Action?.ValidValues as ValidValue[];
  return {
    component: component,
    condition: condition,
    action: action,
  };
};

export const getExistingCodes = async (
  dateCreated: string,
  timeCreated: string
) => {
  const response = await getEvents({
    comment: false,
    completion: true,
    dateCreated: dateCreated,
    timeCreated: timeCreated,
  });
  return response.data?.Completion as AnalysisCode[] | undefined;
};

export const submitCodes = async (
  codes: AnalysisCode[],
  event?: WorkOrderEvents
) => {
  const data = {
    RequestObject: {
      ...event,
      Completion: codes,
    },
  };
  return await updateEvents(data);
};
